// src/pages/BookRide.tsx — HEARTLAS RIDES v8
// ─────────────────────────────────────────────────────────────────────────────
// CHANGELOG v7 → v8
// ✅ Removed the nonexistent `selectServiceFee` import — riderServiceFeeSlice
//    never exported it. Fees now read directly off `state.riderServiceFees`.
//    (Adjust the store key below if your combineReducers uses a different name.)
// ✅ Fixed `fees.guestFee` → `fees.guestsFee` (the actual field the slice returns).
// ✅ `calculateRideEarnings` was being called with `extras`/`selectedExtras`,
//    which the thunk silently ignores — it only accepts a numeric `extraCharges`.
//    Extras were therefore NEVER reflected in the displayed price. Fixed.
// ✅ Rate type now defaults to "perSurge" (Uber-style base+km+min+surge) whenever
//    a driver/listing doesn't set one, per RideRateType in the slice.
// ✅ Stripe call now matches the ShortAccomodationPreview pattern exactly
//    (CheckoutResponse + redirectFromResponse, tolerant of several response shapes).
// ✅ Cloud Function domain fixed: europe-west3-heartlaas (was xsafari).
// ✅ Cosmoola currency (`useCurrency`) used for every price shown, not raw "€".
// ✅ Idempotency key + hard submit-lock ref (state alone isn't race-proof).
// ✅ Return-from-Stripe handling: reads `tripId` from the URL on mount to
//    reattach the live trip listener, plus a fallback pointer listener at
//    `activeRiderTrip/{userId}` for resuming a trip on a fresh page load.
// ✅ Cancelled-checkout recovery via sessionStorage (re-offers the same driver).
// ✅ Realtime DB connectivity banner (so a dropped socket doesn't fail silently).
// ✅ Full Heartlas gold/red re-theme, matching AtlasHome's design tokens.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useEffect, useRef, useState, useCallback, useMemo, memo,
  forwardRef, useImperativeHandle,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { ref as dbRef, onValue, off, get as dbGet } from "firebase/database";
import { db, rtdb } from "../../../firebase";
import type { RootState, AppDispatch } from "../../../redux/store";
import { calculateRideEarnings } from "../../../redux/riderServiceFeeSlice";
import type { RideRateType } from "../../../redux/riderServiceFeeSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import {
  ChevronLeft, Clock, Car, Plus, Minus, CheckCircle, X,
  Locate, Star, Users, Diamond, Bell, UserPlus, Info,
  ArrowLeftRight, ArrowRight, WifiOff, ChevronUp, ChevronDown,
  Zap, Navigation2, Dog, Baby, Home, RotateCcw, AlertCircle,
  Loader2, Flag, PhoneCall, MapPin, CarFront, CloudOff, Sparkles,
} from "lucide-react";
import IMG_CAR from "../../../assets/ride/economy.png";
import IMG_LUXE from "../../../assets/ride/Luxe.png";
import IMG_S    from "../../../assets/ride/S.png";
import IMG_XL   from "../../../assets/ride/XL.png";
import {
  CAR_TYPES, BIKE_TYPES, JET_TYPES, CAR_BRANDS,
  CLASS_TYPE, CAR_SIZE, carSizeName,
} from "../../../CATEGORIES/OperatorCategories";

/* ─── Cloud Function endpoints ───────────────────────────────────────────── */
const FN_BASE               = "https://europe-west1-heartlas.cloudfunctions.net";
const RIDE_CHECKOUT_URL     = `${FN_BASE}/createRideCheckoutSession`;
const CANCEL_RIDE_URL       = `${FN_BASE}/cancelRide`;

/* ─── Constants ──────────────────────────────────────────────────────────── */
const DEFAULT_CENTER = { lat: -1.2921, lng: 36.8219 };
const DRIVERS_INITIAL = 5; // show 5, then "View More"
const PENDING_RIDE_KEY = "heartlas_pending_ride"; // sessionStorage — survives the Stripe round-trip

const ALL_VEHICLE_TYPES = [...(CAR_TYPES as any[]), ...(BIKE_TYPES as any[]), ...(JET_TYPES as any[])];
const ALL_BRANDS        = CAR_BRANDS as any[]; // brands + luxury tiers are unified now — nothing to merge
/** Luxury is now a *listing's* classType (host-editable, independent of the brand's
 *  own default), not something derived from the brand id. */
const LUXURY_CLASS_IDS  = new Set<number>([CLASS_TYPE.LUXURY, CLASS_TYPE.ULTRA_LUXURY]);
const isLuxuryClass     = (classType?: number): boolean => classType !== undefined && LUXURY_CLASS_IDS.has(classType);

type RRT = RideRateType; // "perPerson" | "perPersonPerKm" | "perKm" | "perDay" | "perCar" | "perSeat" | "perSurge"
const DEFAULT_RATE_TYPE: RRT = "perKm";
/** Every place we used to fall back to "perKm" now falls back to perSurge. */
const resolveRateType = (rt?: string | null): RRT => ((rt as RRT) || DEFAULT_RATE_TYPE);

const RATE_LABEL: Record<string, string> = {
  perKm: "Per Kilometre", perPerson: "Per Person", perDay: "Per Day",
  perCar: "Flat Rate / Trip", perSeat: "Per Seat", perPersonPerKm: "Per Person / Km",
  perSurge: "Dynamic Fare",
};
const RATE_SHORT: Record<string, string> = {
  perKm: "/km", perPerson: "/person", perDay: "/day",
  perCar: "/trip", perSeat: "/seat", perPersonPerKm: "/p·km",
  perSurge: "/trip",
};

const TRIP_STATUS_CFG: Record<string, { label: string; color: string; sub: string }> = {
  requested:      { label: "Finding Your Driver…", color: "#8A7660", sub: "Hang tight, we're matching you" },
  accepted:       { label: "Driver Accepted!",      color: "#1A9E6E", sub: "Driver is on the way" },
  arriving:       { label: "Driver Arriving",       color: "#4A9EFF", sub: "Almost there…" },
  ongoing:        { label: "Trip in Progress",      color: "#1A9E6E", sub: "You're on your way" },
  completed:      { label: "Trip Complete 🎉",      color: "#1A9E6E", sub: "Thanks for riding with Heartlas" },
  cancelled:      { label: "Ride Cancelled",        color: "#C8202E", sub: "Your ride was cancelled" },
  no_driver:      { label: "No Driver Available",   color: "#C8202E", sub: "No drivers found nearby" },
  finding_driver: { label: "Finding Driver…",       color: "#8A7660", sub: "Searching nearby drivers" },
};

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const vehicleTypeName = (id: number) => (ALL_VEHICLE_TYPES.find((v: any) => v.id === id) as any)?.name ?? "Ride";
const brandName       = (id: number) => (ALL_BRANDS.find((b: any) => b.id === id) as any)?.name ?? "";
const discountPct     = (d: any): number => !d ? 0 : d.is80 ? 80 : d.is50 ? 50 : d.is25 ? 25 : d.is10 ? 10 : d.is5 ? 5 : 0;
const sizeImage = (carSizeId?: number, classType?: number) => {
  if (isLuxuryClass(classType)) return IMG_LUXE;
  return (carSizeId === CAR_SIZE.XL || carSizeId === CAR_SIZE.XXL) ? IMG_XL : IMG_S;
};
const haversineKm = (la1: number, lo1: number, la2: number, lo2: number) => {
  const R = 6371, dLa = (la2 - la1) * Math.PI / 180, dLo = (lo2 - lo1) * Math.PI / 180;
  const a = Math.sin(dLa / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLo / 2) ** 2;
  return +(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
};
/** Sums only the extras the rider actually selected, using the driver's own price list.
 *  This is what was missing before — the thunk needs a number, not a map of keys. */
const sumSelectedExtras = (allExtras: Record<string, number> | undefined, selected: string[]): number => {
  if (!allExtras || !selected?.length) return 0;
  return selected.reduce((sum, key) => sum + (Number(allExtras[key]) || 0), 0);
};
/** RFC4122-ish fallback for browsers/webviews without crypto.randomUUID. */
const makeIdempotencyKey = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `rk_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

/* ─── Reverse geocode lat/lng → human address ────────────────────────────── */
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const g = (window as any).google?.maps;
    if (!g) return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    return await new Promise(resolve => {
      new g.Geocoder().geocode({ location: { lat, lng } }, (results: any[], status: string) => {
        if (status === "OK" && results?.[0]) {
          const r = results[0];
          const route    = r.address_components?.find((c: any) => c.types.includes("route"))?.long_name;
          const locality = r.address_components?.find((c: any) => c.types.includes("locality"))?.long_name;
          resolve(route && locality ? `${route}, ${locality}` : r.formatted_address);
        } else {
          resolve(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        }
      });
    });
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
}

/* ─── Map style — warm, brand-aligned (cream/ink instead of pure grayscale) ── */
const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#4A3B2C" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#EEDFC4" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#FCF6EA" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#D4900F" }, { weight: 0.6 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#1B0E06" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#EAE2D3" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#F5EEDD" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#EFE7D4" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#FBF6EE" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#DDCBA6" }] },
];

/* ─── SVG Markers — ink pickup pin, brand-red dropoff pin ────────────────── */
const mkPickup = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='44' viewBox='0 0 32 44'>
    <path d='M16 0C7.2 0 0 7.2 0 16c0 12.5 16 28 16 28s16-15.5 16-28C32 7.2 24.8 0 16 0z' fill='%231B0E06'/>
    <circle cx='16' cy='16' r='7' fill='white'/>
    <circle cx='16' cy='16' r='3.5' fill='%23D4900F'/>
  </svg>`);

const mkDropoff = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='44' viewBox='0 0 32 44'>
    <path d='M16 0C7.2 0 0 7.2 0 16c0 12.5 16 28 16 28s16-15.5 16-28C32 7.2 24.8 0 16 0z' fill='%23C8202E'/>
    <circle cx='16' cy='16' r='7' fill='white'/>
    <circle cx='16' cy='16' r='3.5' fill='%23C8202E'/>
  </svg>`);

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface LatLng { lat: number; lng: number; }
interface Driver {
  id: string; listingId: string; driverId: string; hostId?: string;
  driverName?: string; fullName?: string; profilePhoto?: string;
  brand?: number; brandId?: string; model?: string; vehicleType?: number;
  // DriverCars.tsx saves the size picker as a numeric CAR_SIZES id under "carSize"
  // (e.g. CAR_SIZE.XL === 314) and class (economy/business/luxury/ultraLuxury) as
  // its own numeric "classType" id — independent of the brand's default class.
  carSize?: number; classType?: number; rideType?: number;
  rate?: number; rateType?: RRT; capacity?: number; seats?: number; rating?: number;
  rideSharing?: boolean; extras?: Record<string, number>; discountApplied?: any;
  currency?: string; operatorId?: string; phone?: string;
  // perSurge fields — used when rateType resolves to "perSurge" (the new default)
  baseFare?: number; perKmRate?: number; perMinuteRate?: number; surgeMultiplier?: number;
  livePos: LatLng; heading?: number; distKm?: number; eta?: number;
}

/* ─── CSS — Heartlas gold/red brand theme (matches AtlasHome tokens) ─────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400;1,600&family=Cormorant+Garamond:wght@600;700&family=Syne:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --w:#FFFFFF;--iv:#FBF6EE;--cr:#FCF6EA;--st:#EEDFC4;--ms:#C9B79B;
  --wm:#8A7660;--dk:#1B0E06;--ch:#1B0E06;--ng:#1B0E06;
  --rd:#C8202E;--rdd:#9E1521;--rds:#FBE7E9;
  --gd:#F2A93E;--gdd:#D4900F;--gdp:#FCEFD4;--gdb:rgba(242,169,62,.38);
  --gn:#1A9E6E;--bl:#4A9EFF;
  --grad:linear-gradient(135deg,#1B0E06 0%,#D4900F 35%,#C8202E 65%,#F2A93E 100%);
  --gradh:linear-gradient(90deg,#1B0E06 0%,#D4900F 25%,#C8202E 60%,#F2A93E 100%);
  --serif:'Playfair Display',Georgia,serif;
  --cg:'Cormorant Garamond',Georgia,serif;
  --sans:'Syne',-apple-system,sans-serif;
  --s1:0 2px 8px rgba(27,14,6,.06);--s2:0 10px 34px rgba(27,14,6,.12);--s3:0 4px 16px rgba(200,32,46,.28);
}
html,body{height:100%;overflow:hidden;background:var(--iv);}
::-webkit-scrollbar{width:0;height:0;}
.xr{width:100vw;height:100dvh;font-family:var(--sans);color:var(--ng);display:flex;flex-direction:column;overflow:hidden;background:var(--w);}

@keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes xSpin{to{transform:rotate(360deg);}}
@keyframes xPulse{0%,100%{opacity:.4;transform:scale(1);}50%{opacity:1;transform:scale(1.3);}}
@keyframes xSlide{from{transform:translateY(100%);}to{transform:translateY(0);}}
@keyframes floatIn{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}

/* HEADER */
.xr-topbar{height:3px;background:var(--grad);background-size:200% 100%;animation:gradSpin 4s ease infinite;flex-shrink:0;}
.xr-hdr{flex-shrink:0;height:56px;background:var(--w);border-bottom:1px solid var(--st);display:flex;align-items:stretch;z-index:50;box-shadow:var(--s1);}
.xr-logo{display:flex;align-items:center;padding:0 20px;border-right:1px solid var(--st);cursor:pointer;flex-shrink:0;gap:9px;}
.xr-logo-ring{position:relative;width:26px;height:26px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.xr-logo-ring::before{content:"";position:absolute;inset:-2px;border-radius:11px;background:var(--grad);background-size:300% 100%;animation:gradSpin 3s linear infinite;}
.xr-logo-ring-inner{position:relative;width:100%;height:100%;border-radius:9px;background:var(--w);display:flex;align-items:center;justify-content:center;}
.xr-logo-x{font-family:var(--serif);font-size:19px;font-weight:900;letter-spacing:-.5px;
  background:var(--gradh);background-size:200% 100%;animation:gradSpin 5s ease infinite;
  -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;}
.xr-hdr-c{flex:1;display:flex;align-items:center;padding:0 14px;gap:10px;min-width:0;}
.xr-back{width:34px;height:34px;border-radius:50%;background:var(--cr);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ng);flex-shrink:0;transition:all .2s;}
.xr-back:hover{background:var(--ng);color:var(--w);}
.xr-hdr-lbl{font-size:10px;font-weight:700;letter-spacing:3.5px;text-transform:uppercase;color:var(--ng);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.xr-live{display:flex;align-items:center;gap:6px;background:rgba(26,158,110,.08);border:1px solid rgba(26,158,110,.2);padding:4px 11px;border-radius:30px;flex-shrink:0;}
.xr-live-dot{width:6px;height:6px;border-radius:50%;background:var(--gn);animation:xPulse 1.8s ease-in-out infinite;}
.xr-live-txt{color:var(--gn);font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;}
.xr-hdr-r{display:flex;align-items:center;gap:8px;padding-right:14px;}
.xr-ibtn{width:34px;height:34px;border-radius:50%;background:var(--cr);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ng);transition:all .2s;}
.xr-ibtn:hover{background:var(--rd);border-color:var(--rd);color:var(--w);}

/* CONNECTIVITY / NOTICE BANNERS */
.xr-banner{display:flex;align-items:center;gap:9px;padding:9px 16px;font-size:11.5px;font-weight:700;flex-shrink:0;animation:floatIn .25s ease both;}
.xr-banner-offline{background:#3A2A1C;color:#F5DFC0;}
.xr-banner-resume{background:var(--gdp);color:var(--gdd);border-bottom:1px solid var(--gdb);}
.xr-banner-resume button{margin-left:auto;background:var(--ng);color:#fff;border:none;padding:6px 13px;border-radius:20px;font-size:10px;font-weight:800;letter-spacing:.5px;cursor:pointer;flex-shrink:0;}
.xr-banner-close{background:none;border:none;color:inherit;opacity:.6;cursor:pointer;display:flex;flex-shrink:0;}

/* BODY */
.xr-body{flex:1;display:flex;overflow:hidden;position:relative;}
@media(min-width:900px){
  .xr-panel-col{width:400px;flex-shrink:0;background:var(--w);border-right:1px solid var(--st);display:flex;flex-direction:column;overflow-y:auto;position:relative;z-index:10;}
  .xr-map-col{flex:1;position:relative;min-width:0;}
  .xr-mob-sheet{display:none!important;}
}
@media(max-width:899px){
  .xr-body{flex-direction:column;}
  .xr-panel-col{display:none!important;}
  .xr-map-col{flex:1;min-height:0;position:relative;}
  .xr-mob-sheet{position:absolute;left:0;right:0;bottom:0;background:var(--w);border-top-left-radius:22px;border-top-right-radius:22px;border-top:1.5px solid var(--st);box-shadow:0 -10px 44px rgba(27,14,6,.14);z-index:20;will-change:transform;touch-action:none;overflow:hidden;}
  .xr-sheet-scroll{overscroll-behavior:contain;}
}

/* MAP */
.xr-map-wrap{width:100%;height:100%;position:relative;}
.xr-map-loading{position:absolute;inset:0;background:var(--cr);display:flex;align-items:center;justify-content:center;z-index:5;}
.xr-map-spin{width:30px;height:30px;border-radius:50%;border:2px solid var(--st);border-top-color:var(--rd);animation:xSpin .85s linear infinite;}
.xr-overlays{position:absolute;top:12px;left:12px;right:12px;z-index:10;display:flex;justify-content:space-between;align-items:flex-start;pointer-events:none;}
.xr-eta-col{display:flex;flex-direction:column;gap:6px;}
.xr-eta-pill{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,.92);backdrop-filter:blur(10px);border:1.5px solid var(--st);padding:8px 14px;border-radius:30px;box-shadow:var(--s2);pointer-events:all;}
.xr-eta-pill-red{border-color:rgba(200,32,46,.25);}
.xr-eta-num{font-family:var(--serif);font-size:15px;font-weight:700;color:var(--ng);}
.xr-eta-lbl{font-size:9px;color:var(--wm);font-weight:600;text-transform:uppercase;letter-spacing:1px;margin-top:1px;}
.xr-cnt-pill{display:flex;align-items:center;gap:7px;background:var(--grad);background-size:200% 100%;animation:gradSpin 6s ease infinite;padding:8px 14px;border-radius:30px;box-shadow:var(--s2);pointer-events:all;}
.xr-cnt-txt{font-size:11px;font-weight:700;color:var(--w);}

/* PANEL */
.xr-panel-top{padding:20px 20px 0;}
.xr-eyebrow{font-size:8px;font-weight:700;letter-spacing:4px;color:var(--gdd);text-transform:uppercase;margin-bottom:5px;}
.xr-panel-title{font-family:var(--serif);font-size:28px;font-weight:700;color:var(--ng);line-height:1.1;letter-spacing:-.3px;margin-bottom:3px;}
.xr-panel-sub{font-size:11px;color:var(--wm);font-weight:500;line-height:1.5;margin-bottom:16px;}

/* ADDRESS */
.xr-addr-card{background:var(--w);border:1.5px solid var(--st);margin-bottom:12px;overflow:hidden;box-shadow:var(--s1);border-radius:14px;}
.xr-addr-row{display:flex;align-items:center;gap:10px;padding:12px 14px;}
.xr-addr-icon{display:flex;flex-direction:column;align-items:center;gap:2px;flex-shrink:0;width:20px;}
.xr-pin-pickup{color:var(--ng);}
.xr-pin-dropoff{color:var(--rd);}
.xr-dot-line{width:1.5px;height:14px;background:var(--st);margin:0 auto;}
.xr-addr-f{flex:1;min-width:0;}
.xr-addr-lbl{font-size:7.5px;font-weight:700;letter-spacing:2.5px;color:var(--ms);text-transform:uppercase;margin-bottom:4px;}
.xr-addr-inp{width:100%;background:transparent;border:none;outline:none;font-size:13px;font-weight:600;color:var(--ng);font-family:var(--sans);padding:0;line-height:1.4;}
.xr-addr-inp::placeholder{color:var(--ms);font-weight:500;}
.xr-locate-btn{width:30px;height:30px;border-radius:50%;background:var(--w);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--gdd);flex-shrink:0;transition:all .2s;}
.xr-locate-btn:hover{background:var(--gd);color:#fff;border-color:var(--gd);}
.xr-addr-sep{display:flex;align-items:center;gap:8px;padding:4px 14px;background:var(--cr);border-top:1px solid var(--st);border-bottom:1px solid var(--st);}
.xr-sep-line{flex:1;height:1px;background:var(--st);}
.xr-swap-btn{width:24px;height:24px;border-radius:50%;background:var(--w);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--wm);transition:all .2s;}
.xr-swap-btn:hover{background:var(--rd);color:var(--w);border-color:var(--rd);}

/* FOR OTHER */
.xr-other-toggle{display:flex;align-items:center;justify-content:space-between;padding:11px 0;cursor:pointer;}
.xr-other-lbl{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:600;color:var(--wm);}
.xr-other-lbl.on{color:var(--ng);}
.xr-tog{width:40px;height:22px;border-radius:11px;background:var(--st);position:relative;transition:background .25s;flex-shrink:0;}
.xr-tog.on{background:var(--gn);}
.xr-thumb{width:16px;height:16px;border-radius:50%;background:var(--w);position:absolute;top:3px;left:3px;transition:left .25s;box-shadow:0 1px 4px rgba(0,0,0,.15);}
.xr-thumb.on{left:21px;}
.xr-other-note{display:flex;gap:8px;background:var(--gdp);border:1px solid var(--gdb);padding:10px 12px;margin-bottom:14px;font-size:11px;color:var(--dk);line-height:1.6;border-radius:10px;}

/* VEHICLE TYPE FILTER */
.xr-filter-lbl{font-size:8px;font-weight:700;letter-spacing:2.5px;color:var(--ms);text-transform:uppercase;margin-bottom:9px;}
.xr-type-scroll{display:flex;gap:6px;overflow-x:auto;scrollbar-width:none;padding-bottom:2px;margin-bottom:14px;}
.xr-type-scroll::-webkit-scrollbar{display:none;}
.xr-type-chip{display:flex;align-items:center;gap:5px;flex-shrink:0;padding:6px 13px;border:1.5px solid var(--st);background:var(--w);cursor:pointer;font-size:10px;font-weight:700;color:var(--ch);white-space:nowrap;border-radius:30px;transition:all .2s;font-family:var(--sans);text-transform:uppercase;letter-spacing:.5px;}
.xr-type-chip:hover{border-color:var(--rd);}
.xr-type-chip.on{background:var(--ng);border-color:var(--ng);color:var(--w);}

/* SECTION DIVIDER */
.xr-section-div{display:flex;align-items:center;gap:10px;margin:4px 0 14px;padding:0 20px;}
.xr-div-line{flex:1;height:1px;background:var(--st);}
.xr-rate-badge{display:inline-flex;align-items:center;gap:5px;background:var(--gdp);border:1px solid var(--gdb);padding:4px 11px;border-radius:20px;font-size:9px;font-weight:800;color:var(--gdd);white-space:nowrap;flex-shrink:0;}

/* DRIVER LIST */
.xr-dlist{padding:0 16px 40px;}
.xr-dlist-hdr{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:12px;padding:0 4px;}
.xr-dlist-title{font-family:var(--serif);font-size:20px;font-weight:700;color:var(--ng);}
.xr-dlist-cnt{font-size:9px;color:var(--wm);font-weight:700;letter-spacing:1.5px;text-transform:uppercase;}

/* VIEW MORE */
.xr-view-more{width:100%;padding:13px 0;background:var(--cr);border:1.5px solid var(--st);cursor:pointer;font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--ch);font-family:var(--sans);display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;margin-bottom:14px;border-radius:12px;}
.xr-view-more:hover{background:var(--ng);color:var(--w);border-color:var(--ng);}

/* DRIVER CARD */
.xr-card{background:var(--w);border:1.5px solid var(--st);margin-bottom:12px;overflow:hidden;cursor:pointer;transition:all .25s;position:relative;box-shadow:var(--s1);border-radius:16px;}
.xr-card:hover{border-color:var(--gd);box-shadow:var(--s2);transform:translateY(-2px);}
.xr-card.sel{border-color:var(--gn);box-shadow:0 0 0 3px rgba(26,158,110,.12),var(--s2);}
.xr-near-tag{position:absolute;top:0;left:0;background:var(--grad);background-size:200% 100%;animation:gradSpin 5s ease infinite;color:var(--w);font-size:7.5px;font-weight:800;letter-spacing:2px;text-transform:uppercase;padding:4px 12px;display:flex;align-items:center;gap:3px;border-bottom-right-radius:10px;}
.xr-card-hdr{display:flex;align-items:center;gap:11px;padding:14px 14px 10px;}
.xr-av-wrap{position:relative;flex-shrink:0;}
.xr-av{width:44px;height:44px;border-radius:50%;object-fit:cover;border:2px solid var(--st);}
.xr-av-fb{width:44px;height:44px;border-radius:50%;background:var(--gdp);border:2px solid var(--st);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:18px;font-weight:700;color:var(--gdd);}
.xr-av-ring{position:absolute;bottom:-1px;right:-1px;width:13px;height:13px;border-radius:50%;background:var(--gn);border:2.5px solid var(--w);}
.xr-drv-info{flex:1;min-width:0;}
.xr-drv-name{font-size:13px;font-weight:700;color:var(--ng);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;letter-spacing:-.2px;}
.xr-drv-sub{font-size:10px;color:var(--wm);margin-top:2px;font-weight:500;}
.xr-rate-col{text-align:right;flex-shrink:0;}
.xr-rate-old{font-size:9px;color:var(--ms);text-decoration:line-through;}
.xr-rate-main{font-family:var(--serif);font-size:21px;font-weight:700;color:var(--ng);line-height:1.1;}
.xr-rate-unit{font-size:8.5px;color:var(--wm);font-weight:700;letter-spacing:1px;text-transform:uppercase;}
.xr-band{height:86px;position:relative;overflow:hidden;display:flex;align-items:center;justify-content:center;}
.xr-band-lt{background:var(--cr);}
.xr-band-dk{background:var(--ng);}
.xr-band-img{height:80px;object-fit:contain;position:relative;z-index:1;filter:drop-shadow(0 4px 10px rgba(27,14,6,.14));}
.xr-band-rule{position:absolute;bottom:0;left:0;right:0;height:1.5px;}
.xr-band-rule-lt{background:linear-gradient(90deg,transparent,rgba(212,144,15,.3),transparent);}
.xr-band-rule-dk{background:linear-gradient(90deg,transparent,rgba(255,255,255,.3),transparent);}
.xr-size-tag{position:absolute;top:7px;left:9px;background:rgba(255,255,255,.92);backdrop-filter:blur(8px);padding:2px 7px;font-size:8.5px;font-weight:700;color:var(--ch);border-radius:5px;}
.xr-size-tag-dk{background:rgba(0,0,0,.55);color:var(--w);}
.xr-luxe-tag{position:absolute;bottom:7px;left:9px;display:flex;align-items:center;gap:3px;background:rgba(255,255,255,.92);border:1px solid var(--gdb);padding:2px 8px;color:var(--gdd);font-size:7.5px;font-weight:800;letter-spacing:2px;text-transform:uppercase;border-radius:5px;}
.xr-disc-tag{position:absolute;top:7px;right:9px;background:var(--rd);color:var(--w);font-size:8.5px;font-weight:800;padding:2px 7px;border-radius:5px;}
.xr-card-body{padding:9px 14px 0;}
.xr-car-name{font-size:12px;font-weight:700;color:var(--ng);letter-spacing:-.1px;margin-bottom:4px;}
.xr-rt-chip{display:inline-flex;align-items:center;gap:4px;background:var(--gdp);border:1px solid var(--gdb);padding:2px 8px;border-radius:10px;font-size:8px;font-weight:700;color:var(--gdd);margin-bottom:6px;}
.xr-stats{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:7px;}
.xr-stat{display:flex;align-items:center;gap:3px;font-size:10px;color:var(--wm);font-weight:600;}
.xr-stat-gd{color:var(--gdd);}
.xr-extras{display:flex;flex-wrap:wrap;gap:4px;margin-bottom:9px;}
.xr-epip{display:flex;align-items:center;gap:2px;background:var(--cr);border:1px solid var(--st);padding:2px 7px;border-radius:20px;font-size:8.5px;font-weight:700;color:var(--ch);}
.xr-book-btn{width:100%;padding:11px 0;background:var(--gn);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:7px;font-size:9.5px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--w);font-family:var(--sans);transition:all .2s;margin-top:9px;border-radius:10px;}
.xr-book-btn:hover{background:#167a56;box-shadow:0 6px 18px rgba(26,158,110,.3);}
.xr-book-gd{background:var(--grad);background-size:200% 100%;}
.xr-book-gd:hover{filter:brightness(1.08);}
.xr-loader{display:flex;flex-direction:column;align-items:center;padding:50px 20px;gap:13px;}
.xr-spin-sm{width:28px;height:28px;border-radius:50%;border:2px solid var(--st);border-top-color:var(--rd);animation:xSpin .85s linear infinite;}
.xr-loader-txt{font-size:11px;color:var(--wm);letter-spacing:.5px;font-weight:600;}
.xr-empty{display:flex;flex-direction:column;align-items:center;padding:50px 20px;gap:10px;text-align:center;}
.xr-empty-ttl{font-family:var(--serif);font-size:18px;font-weight:700;color:var(--ng);}
.xr-empty-sub{font-size:11px;color:var(--wm);line-height:1.7;max-width:200px;}

/* SHEET */
.xr-sheet-handle{width:34px;height:3.5px;background:var(--ms);border-radius:2px;margin:11px auto 4px;}
.xr-sheet-peek{display:flex;align-items:center;justify-content:space-between;padding:9px 16px 11px;border-bottom:1px solid var(--cr);}
.xr-sheet-peek-l{display:flex;align-items:center;gap:10px;}
.xr-sheet-ttl{font-size:13px;font-weight:700;color:var(--ng);}
.xr-sheet-sub{font-size:10px;color:var(--wm);margin-top:1px;}
.xr-expand{display:flex;align-items:center;gap:3px;background:var(--cr);border:1px solid var(--st);padding:5px 11px;border-radius:30px;cursor:pointer;font-size:9.5px;font-weight:700;color:var(--ch);font-family:var(--sans);}

/* MODAL */
.xr-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.46);z-index:200;display:flex;align-items:flex-end;justify-content:center;backdrop-filter:blur(6px);}
@media(min-width:600px){.xr-backdrop{align-items:center;}.xr-modal{max-width:490px;width:100%;max-height:88dvh;border-radius:20px!important;}}
.xr-modal{background:var(--w);width:100%;max-height:90dvh;border-top-left-radius:20px;border-top-right-radius:20px;overflow-y:auto;animation:xSlide .3s cubic-bezier(.22,1,.36,1) both;}
.xr-modal::-webkit-scrollbar{width:0;}
.xr-modal-handle{width:34px;height:3.5px;background:var(--ms);border-radius:2px;margin:12px auto 0;}
.xr-modal-inner{padding:20px 22px 40px;}
.xr-modal-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px;}
.xr-modal-ttl{font-family:var(--serif);font-size:24px;font-weight:700;color:var(--ng);}
.xr-modal-sub{font-size:10px;color:var(--wm);margin-top:3px;}
.xr-modal-cls{width:32px;height:32px;border-radius:50%;background:var(--cr);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ng);flex-shrink:0;margin-left:10px;transition:all .2s;}
.xr-modal-cls:hover{background:var(--rd);border-color:var(--rd);color:var(--w);}
.xr-ctr-row{display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid var(--cr);}
.xr-ctr-lbl{font-size:13px;font-weight:700;color:var(--ng);}
.xr-ctr-sub{font-size:10px;color:var(--wm);margin-top:1px;}
.xr-ctr-ctrl{display:flex;align-items:center;gap:5px;}
.xr-ctr-btn{width:34px;height:34px;border-radius:50%;border:1.5px solid var(--st);background:var(--w);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ng);transition:all .2s;}
.xr-ctr-btn:hover:not(:disabled){background:var(--ng);color:var(--w);border-color:var(--ng);}
.xr-ctr-btn:disabled{opacity:.28;cursor:default;}
.xr-ctr-val{width:30px;text-align:center;font-size:16px;font-weight:700;color:var(--ng);}
.xr-seat-grid{display:flex;flex-wrap:wrap;gap:7px;margin:11px 0 4px;}
.xr-seat-chip{display:flex;align-items:center;gap:4px;padding:8px 14px;border:1.5px solid var(--st);border-radius:30px;cursor:pointer;background:var(--w);font-size:12px;font-weight:700;color:var(--ng);font-family:var(--sans);transition:all .2s;}
.xr-seat-chip.on{background:var(--ng);border-color:var(--ng);color:var(--w);}
.xr-info-box{display:flex;gap:9px;background:var(--gdp);border:1px solid var(--gdb);padding:12px 13px;margin:12px 0;border-radius:12px;}
.xr-info-txt{font-size:11px;color:var(--dk);line-height:1.65;}
.xr-ext-sec{margin:16px 0 0;}
.xr-ext-lbl{font-size:8px;font-weight:700;letter-spacing:2.5px;color:var(--ms);text-transform:uppercase;margin-bottom:9px;}
.xr-ext-row{display:flex;align-items:center;padding:10px 0;border-bottom:1px solid var(--cr);gap:10px;cursor:pointer;}
.xr-ext-chk{width:22px;height:22px;border-radius:50%;border:1.5px solid var(--st);flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:all .2s;}
.xr-ext-chk.on{background:var(--gn);border-color:var(--gn);}
.xr-ext-name{flex:1;font-size:12px;font-weight:600;color:var(--ng);}
.xr-ext-price{font-size:12px;font-weight:700;color:var(--ng);}
.xr-price-box{background:var(--cr);border:1px solid var(--st);padding:16px;margin:18px 0 14px;border-radius:14px;}
.xr-price-row{display:flex;justify-content:space-between;align-items:center;padding:4px 0;}
.xr-price-lbl{font-size:12px;color:var(--dk);}
.xr-price-val{font-size:12px;font-weight:700;color:var(--ng);}
.xr-price-div{height:1px;background:var(--st);margin:7px 0;}
.xr-price-ttl-lbl{font-size:13px;font-weight:700;color:var(--ng);}
.xr-price-ttl-val{font-family:var(--serif);font-size:23px;font-weight:800;
  background:var(--gradh);background-size:200% 100%;animation:gradSpin 3s ease infinite;
  -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;}
.xr-price-note{font-size:9.5px;color:var(--wm);margin-top:7px;}
.xr-price-gn{color:var(--gn);}
.xr-confirm-btn{width:100%;padding:15px 0;background:var(--grad);background-size:200% 100%;animation:gradSpin 6s ease infinite;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;font-size:10px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--w);font-family:var(--sans);transition:all .2s;border-radius:12px;box-shadow:var(--s3);}
.xr-confirm-btn:hover:not(:disabled){filter:brightness(1.08);}
.xr-confirm-btn:disabled{opacity:.45;cursor:not-allowed;animation:none;}
.xr-err-box{display:flex;align-items:center;gap:9px;background:var(--rds);border:1px solid rgba(200,32,46,.3);padding:10px 12px;margin-bottom:12px;color:var(--rd);font-size:12px;font-weight:600;border-radius:10px;}

/* TRIP SUMMARY */
.xr-trip-card{padding:20px 16px;}
.xr-trip-status{display:flex;align-items:center;gap:10px;padding:14px;margin-bottom:14px;border:1.5px solid;border-radius:14px;}
.xr-trip-status-icon{flex-shrink:0;}
.xr-trip-status-lbl{font-size:15px;font-weight:800;}
.xr-trip-status-sub{font-size:10px;color:var(--wm);margin-top:2px;}
.xr-trip-driver-row{display:flex;align-items:center;gap:12px;background:var(--cr);border:1px solid var(--st);padding:14px;margin-bottom:12px;border-radius:14px;}
.xr-trip-av{width:46px;height:46px;border-radius:50%;background:var(--gdp);border:2px solid var(--st);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:18px;font-weight:700;color:var(--gdd);flex-shrink:0;overflow:hidden;}
.xr-trip-av img{width:100%;height:100%;object-fit:cover;}
.xr-trip-drv-info{flex:1;min-width:0;}
.xr-trip-drv-name{font-size:14px;font-weight:800;color:var(--ng);}
.xr-trip-drv-sub{font-size:10px;color:var(--wm);margin-top:2px;}
.xr-trip-route{display:flex;flex-direction:column;gap:8px;margin-bottom:14px;}
.xr-trip-addr-row{display:flex;align-items:center;gap:10px;}
.xr-trip-addr-txt{font-size:12px;font-weight:600;color:var(--dk);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.xr-trip-price-row{display:flex;justify-content:space-between;align-items:center;background:var(--cr);border:1px solid var(--st);padding:12px 14px;margin-bottom:12px;border-radius:14px;}
.xr-trip-price-lbl{font-size:11px;color:var(--wm);}
.xr-trip-price-val{font-family:var(--serif);font-size:20px;font-weight:700;color:var(--ng);}
.xr-trip-cancel-btn{width:100%;padding:12px 0;background:none;border:1px solid rgba(200,32,46,.35);cursor:pointer;color:var(--rd);font-size:11px;font-weight:700;font-family:var(--sans);transition:all .15s;display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:8px;border-radius:12px;}
.xr-trip-cancel-btn:hover{background:var(--rds);}
.xr-trip-done-btn{width:100%;padding:14px 0;background:var(--ng);border:none;cursor:pointer;color:var(--w);font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;font-family:var(--sans);display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;border-radius:12px;}
.xr-trip-done-btn:hover{background:var(--rd);}
.xr-trip-eta-badge{display:flex;align-items:center;gap:6px;background:rgba(26,158,110,.08);border:1px solid rgba(26,158,110,.2);padding:6px 14px;border-radius:30px;font-size:12px;font-weight:700;color:var(--gn);}

.pac-container{z-index:99999!important;border:1px solid #EEDFC4!important;border-top:none!important;background:#fff!important;font-family:'Syne',-apple-system,sans-serif!important;box-shadow:0 8px 32px rgba(27,14,6,.12)!important;}
.pac-item{font-size:12px!important;color:#1B0E06!important;padding:9px 14px!important;cursor:pointer!important;}
.pac-item:hover{background:#FCF6EA!important;}
.pac-item-query{font-weight:700!important;}
`;

/* ─── AddressForm ─────────────────────────────────────────────────────────── */
interface AddressFormProps {
  pickupAddr: string; dropoffAddr: string; forOther: boolean;
  activeCat: number | null; availTypes: any[];
  onPickupChange: (v: string) => void; onDropoffChange: (v: string) => void;
  onForOtherToggle: () => void; onLocateMe: () => void;
  onSwap: () => void; onCatChange: (id: number | null) => void;
}
export interface AddressFormHandle {
  pickupInput: HTMLInputElement | null;
  dropoffInput: HTMLInputElement | null;
}
const AddressForm = forwardRef<AddressFormHandle, AddressFormProps>((
  { pickupAddr, dropoffAddr, forOther, activeCat, availTypes,
    onPickupChange, onDropoffChange, onForOtherToggle, onLocateMe, onSwap, onCatChange }, ref
) => {
  const pickupRef  = useRef<HTMLInputElement>(null);
  const dropoffRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => ({
    get pickupInput()  { return pickupRef.current; },
    get dropoffInput() { return dropoffRef.current; },
  }));
  return (<>
    <div className="xr-addr-card">
      <div className="xr-addr-row">
        <div className="xr-addr-icon">
          <MapPin size={14} className="xr-pin-pickup" />
          <div className="xr-dot-line" />
        </div>
        <div className="xr-addr-f">
          <div className="xr-addr-lbl">Pick-up</div>
          <input ref={pickupRef} className="xr-addr-inp" placeholder="Enter pickup location"
            defaultValue={pickupAddr} onChange={e => onPickupChange(e.target.value)} />
        </div>
        <button className="xr-locate-btn" onClick={onLocateMe}><Locate size={12} /></button>
      </div>
      <div className="xr-addr-sep">
        <div className="xr-sep-line" />
        <button className="xr-swap-btn" onClick={onSwap}><ArrowLeftRight size={10} /></button>
        <div className="xr-sep-line" />
      </div>
      <div className="xr-addr-row">
        <div className="xr-addr-icon">
          <MapPin size={14} className="xr-pin-dropoff" />
        </div>
        <div className="xr-addr-f">
          <div className="xr-addr-lbl">Destination</div>
          <input ref={dropoffRef} className="xr-addr-inp" placeholder="Where to? (optional)"
            defaultValue={dropoffAddr} onChange={e => onDropoffChange(e.target.value)} />
        </div>
      </div>
    </div>
    <div className="xr-other-toggle" onClick={onForOtherToggle}>
      <div className={`xr-other-lbl${forOther ? " on" : ""}`}><UserPlus size={13} /> Booking for someone else</div>
      <div className={`xr-tog${forOther ? " on" : ""}`}><div className={`xr-thumb${forOther ? " on" : ""}`} /></div>
    </div>
    {forOther && (
      <div className="xr-other-note">
        <Info size={12} color="#D4900F" style={{ flexShrink: 0 }} />
        Pickup is set to their address — we show drivers nearest that point.
      </div>
    )}
    {availTypes.length > 0 && (
      <div style={{ marginBottom: 4 }}>
        <div className="xr-filter-lbl">Vehicle Type</div>
        <div className="xr-type-scroll">
          <button className={`xr-type-chip${activeCat === null ? " on" : ""}`} onClick={() => onCatChange(null)}>
            <Car size={11} /> All
          </button>
          {availTypes.map((vt: any) => (
            <button key={vt.id} className={`xr-type-chip${activeCat === vt.id ? " on" : ""}`}
              onClick={() => onCatChange(activeCat === vt.id ? null : vt.id)}>
              <Car size={11} /> {vt.name}
            </button>
          ))}
        </div>
      </div>
    )}
  </>);
});
AddressForm.displayName = "AddressForm";

/* ─── CounterRow ─────────────────────────────────────────────────────────── */
const CounterRow = memo(({ label, sub, value, onInc, onDec, minVal = 0, icon }: any) => (
  <div className="xr-ctr-row">
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon && <span style={{ color: "var(--gdd)", display: "flex" }}>{icon}</span>}
      <div><div className="xr-ctr-lbl">{label}</div>{sub && <div className="xr-ctr-sub">{sub}</div>}</div>
    </div>
    <div className="xr-ctr-ctrl">
      <button className="xr-ctr-btn" onClick={onDec} disabled={value <= minVal}><Minus size={13} /></button>
      <div className="xr-ctr-val">{value}</div>
      <button className="xr-ctr-btn" onClick={onInc}><Plus size={13} /></button>
    </div>
  </div>
));

/* ─── Currency formatting helper (Cosmoola) ───────────────────────────────
   The backend stores rates in EUR (matches the ShortAccomodationPreview
   convention) and Cosmoola converts + symbol-prefixes for display only —
   the *charge* itself is always computed and enforced server-side. */
type Money = (amountEur: number | undefined | null) => string;

/* ─── DriverCard ─────────────────────────────────────────────────────────── */
const DriverCard = memo(({ driver, isSelected, isNearest, onBook, money }: {
  driver: Driver; isSelected: boolean; isNearest: boolean; onBook: () => void; money: Money;
}) => {
  const isLuxe = isLuxuryClass(driver.classType);
  const disc   = discountPct(driver.discountApplied);
  const base   = driver.rate || 0;
  const dispAmt = disc > 0 ? base * (1 - disc / 100) : base;
  const bn     = brandName(driver.brand || 0);
  const vt     = vehicleTypeName(driver.vehicleType || 0);
  const img    = sizeImage(driver.carSize, driver.classType);
  const extras = driver.extras ? Object.entries(driver.extras) : [];
  const rt     = resolveRateType(driver.rateType);
  return (
    <div className={`xr-card${isSelected ? " sel" : ""}`} onClick={onBook}>
      {isNearest && <div className="xr-near-tag"><Zap size={8} /> Nearest</div>}
      <div className="xr-card-hdr" style={isNearest ? { paddingTop: 28 } : undefined}>
        <div className="xr-av-wrap">
          {driver.profilePhoto
            ? <img src={driver.profilePhoto} alt="" className="xr-av" />
            : <div className="xr-av-fb">{(driver.driverName || driver.fullName || "D")[0]}</div>}
          <div className="xr-av-ring" />
        </div>
        <div className="xr-drv-info">
          <div className="xr-drv-name">{driver.driverName || driver.fullName || "Driver"}</div>
          <div className="xr-drv-sub">{driver.rating ? `${driver.rating.toFixed(1)} ★ · ` : "New · "}{vt}</div>
        </div>
        <div className="xr-rate-col">
          {disc > 0 && <div className="xr-rate-old">{money(base)}</div>}
          <div className="xr-rate-main">{rt === "perSurge" ? money(driver.baseFare || dispAmt) : money(dispAmt)}</div>
          <div className="xr-rate-unit">{rt === "perSurge" ? "base fare" : (RATE_SHORT[rt] || "/trip")}</div>
        </div>
      </div>
      <div className={`xr-band ${isLuxe ? "xr-band-dk" : "xr-band-lt"}`}>
        <img src={img} alt={bn || vt} className="xr-band-img" />
        <div className={`xr-band-rule ${isLuxe ? "xr-band-rule-dk" : "xr-band-rule-lt"}`} />
        {driver.carSize !== undefined && <div className={`xr-size-tag${isLuxe ? " xr-size-tag-dk" : ""}`}>{carSizeName(driver.carSize)}</div>}
        {isLuxe && <div className="xr-luxe-tag"><Diamond size={7} /> LUXURY</div>}
        {disc > 0 && <div className="xr-disc-tag">−{disc}%</div>}
      </div>
      <div className="xr-card-body">
        <div className="xr-car-name">{[bn, driver.model].filter(Boolean).join(" ") || vt}</div>
        <div className="xr-rt-chip">{rt === "perSurge" ? <Sparkles size={8} /> : <Car size={8} />}{RATE_LABEL[rt] || rt}</div>
        <div className="xr-stats">
          {driver.eta != null && <span className="xr-stat"><Clock size={9} /> {driver.eta} min away</span>}
          {driver.distKm != null && <span className="xr-stat"><Navigation2 size={9} /> {driver.distKm} km</span>}
          {(driver.seats || driver.capacity) && <span className="xr-stat"><Users size={9} /> {driver.seats || driver.capacity} seats</span>}
          {driver.rating != null && <span className="xr-stat xr-stat-gd"><Star size={9} fill="#D4900F" color="#D4900F" /> {driver.rating.toFixed(1)}</span>}
        </div>
        {extras.length > 0 && (
          <div className="xr-extras">
            {extras.slice(0, 3).map(([k, v]) => <span key={k} className="xr-epip"><Plus size={8} color="#1B0E06" />{k} +{money(Number(v))}</span>)}
          </div>
        )}
        <button className={`xr-book-btn${isNearest ? " xr-book-gd" : ""}`}
          onClick={e => { e.stopPropagation(); onBook(); }}>
          <Car size={12} /> Book Now
        </button>
      </div>
    </div>
  );
});

/* ─── BookingModal ───────────────────────────────────────────────────────── */
function BookingModal({ driver, onClose, onConfirm, fees, booking, bookingErr, money }: {
  driver: Driver; onClose: () => void;
  onConfirm: (opts: any) => void;
  fees: any; booking: boolean; bookingErr: string; money: Money;
}) {
  const rt      = resolveRateType(driver.rateType);
  const maxCap  = parseInt(String(driver.capacity || driver.seats || 8));
  const isLuxe  = isLuxuryClass(driver.classType);
  const disc    = discountPct(driver.discountApplied);
  const dr      = (driver.rate || 0) * (1 - disc / 100);
  const bn      = brandName(driver.brand || 0);
  const vt      = vehicleTypeName(driver.vehicleType || 0);
  const extras  = driver.extras ? Object.entries(driver.extras) : [];

  const [adults,         setAdults]         = useState(1);
  const [children,       setChildren]       = useState(0);
  const [infants,        setInfants]        = useState(0);
  const [pets,           setPets]           = useState(0);
  const [serviceAnimals, setServiceAnimals] = useState(0);
  const [seats,          setSeats]          = useState(1);
  const [days,           setDays]           = useState(1);
  const [selEx,          setSelEx]          = useState<string[]>([]);

  const pax      = adults + children;
  const toggleEx = (k: string) => setSelEx(p => p.includes(k) ? p.filter(e => e !== k) : [...p, k]);
  const isPerP   = rt === "perPerson" || rt === "perPersonPerKm";

  // Extras aren't returned as a field by the fee thunk — compute the display
  // amount locally from the driver's own price list + what's checked.
  const extrasTotal = sumSelectedExtras(driver.extras, selEx);

  const TITLES: Record<string, string> = {
    perPerson: "Who's coming?", perPersonPerKm: "Who's coming?",
    perSeat: "Select Seats", perDay: "Select Duration",
    perCar: "Confirm Booking", perKm: "Confirm Booking",
    perSurge: "Confirm Booking",
  };

  return (
    <div className="xr-backdrop" onClick={onClose}>
      <div className="xr-modal" onClick={e => e.stopPropagation()}>
        <div className="xr-modal-handle" />
        <div className="xr-modal-inner">
          <div className="xr-modal-hdr">
            <div>
              <div className="xr-modal-ttl">{TITLES[rt] || "Confirm Booking"}</div>
              <div className="xr-modal-sub">
                {[bn, driver.model].filter(Boolean).join(" ") || vt}
                {driver.carSize !== undefined ? ` · ${carSizeName(driver.carSize)}` : ""}
                {isLuxe ? " · LUXURY" : ""}
              </div>
            </div>
            <button className="xr-modal-cls" onClick={onClose}><X size={14} /></button>
          </div>

          {bookingErr && (
            <div className="xr-err-box">
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{bookingErr}</span>
            </div>
          )}

          {isPerP && <>
            <CounterRow label="Adults" sub="Age 13+"             value={adults}         minVal={1} icon={<Users size={14} />}
              onInc={() => pax < maxCap && setAdults(a => a + 1)} onDec={() => setAdults(a => Math.max(1, a - 1))} />
            <CounterRow label="Children" sub="Age 2–12"          value={children}       icon={<Baby size={14} />}
              onInc={() => pax < maxCap && setChildren(c => c + 1)} onDec={() => setChildren(c => Math.max(0, c - 1))} />
            <CounterRow label="Infants" sub="Under 2 · Lap seat" value={infants}        icon={<Baby size={12} />}
              onInc={() => setInfants(i => i + 1)} onDec={() => setInfants(i => Math.max(0, i - 1))} />
            <CounterRow label="Pets" sub="Dogs, cats, etc."      value={pets}           icon={<Dog size={14} />}
              onInc={() => setPets(p => p + 1)} onDec={() => setPets(p => Math.max(0, p - 1))} />
            <CounterRow label="Service Animals" sub="Guide / assistance dogs" value={serviceAnimals} icon={<Dog size={14} />}
              onInc={() => setServiceAnimals(s => s + 1)} onDec={() => setServiceAnimals(s => Math.max(0, s - 1))} />
          </>}

          {rt === "perSeat" && <>
            <div className="xr-ext-lbl" style={{ marginTop: 6 }}>Seats Needed</div>
            <div className="xr-seat-grid">
              {Array.from({ length: maxCap }, (_, i) => i + 1).map(n => (
                <button key={n} className={`xr-seat-chip${seats === n ? " on" : ""}`} onClick={() => setSeats(n)}>
                  <Users size={11} />{n}
                </button>
              ))}
            </div>
          </>}

          {rt === "perDay" && (
            <CounterRow label="Days" sub="Full-day hire" value={days} minVal={1} icon={<Clock size={14} />}
              onInc={() => setDays(d => d + 1)} onDec={() => setDays(d => Math.max(1, d - 1))} />
          )}

          {(rt === "perCar" || rt === "perKm") && (
            <div className="xr-info-box">
              <Navigation2 size={14} color="#D4900F" style={{ flexShrink: 0, marginTop: 1 }} />
              <div className="xr-info-txt">
                {rt === "perKm"
                  ? `${money(dr)} per km — final price calculated at journey end based on actual distance.`
                  : `${money(dr)} flat fare for the full journey.`}
              </div>
            </div>
          )}

          {rt === "perSurge" && (
            <div className="xr-info-box">
              <Sparkles size={14} color="#D4900F" style={{ flexShrink: 0, marginTop: 1 }} />
              <div className="xr-info-txt">
                {money(driver.baseFare || 0)} base fare + {money(driver.perKmRate || 0)}/km + {money(driver.perMinuteRate || 0)}/min
                {(driver.surgeMultiplier || 1) > 1 ? ` · ${driver.surgeMultiplier}× surge right now` : ""}. Final price is confirmed at journey end.
              </div>
            </div>
          )}

          {extras.length > 0 && (
            <div className="xr-ext-sec">
              <div className="xr-ext-lbl">Optional Extras</div>
              {extras.map(([k, v]) => (
                <div key={k} className="xr-ext-row" onClick={() => toggleEx(k)}>
                  <div className={`xr-ext-chk${selEx.includes(k) ? " on" : ""}`}>
                    {selEx.includes(k) && <CheckCircle size={11} color="#fff" />}
                  </div>
                  <div className="xr-ext-name">{k}</div>
                  <div className="xr-ext-price">+{money(Number(v))}</div>
                </div>
              ))}
            </div>
          )}

          <div className="xr-price-box">
            <div className="xr-price-row">
              <span className="xr-price-lbl">
                {isPerP ? `${adults} adult${adults > 1 ? "s" : ""}` :
                  rt === "perSeat" ? `${seats} seat${seats > 1 ? "s" : ""}` :
                    rt === "perDay" ? `${days} day${days > 1 ? "s" : ""}` :
                      rt === "perKm" ? "Per km rate" :
                        rt === "perSurge" ? "Estimated fare" : "Base fare"}
              </span>
              <span className="xr-price-val">{money(fees.baseRideCost)}</span>
            </div>
            {extrasTotal > 0 && (
              <div className="xr-price-row">
                <span className="xr-price-lbl">Extras</span>
                <span className="xr-price-val">+{money(extrasTotal)}</span>
              </div>
            )}
            <div className="xr-price-row">
              <span className="xr-price-lbl">Service fee</span>
              <span className="xr-price-val" style={{ color: "var(--wm)" }}>{money(fees.guestsFee)}</span>
            </div>
            {disc > 0 && (
              <div className="xr-price-row">
                <span className="xr-price-lbl xr-price-gn">Discount −{disc}%</span>
                <span className="xr-price-val xr-price-gn">applied</span>
              </div>
            )}
            <div className="xr-price-div" />
            <div className="xr-price-row">
              <span className="xr-price-ttl-lbl">Estimated total</span>
              <span className="xr-price-ttl-val">{money(fees.totalCharged)}</span>
            </div>
            {(rt === "perKm" || rt === "perSurge") && <div className="xr-price-note">* Final price based on actual GPS distance &amp; time — verified server-side</div>}
          </div>

          <button className="xr-confirm-btn" disabled={booking}
            onClick={() => onConfirm({
              adults, children, infants, pets, serviceAnimals, seats, days,
              selectedExtras: selEx, passengers: isPerP ? adults : 1, durationDays: days,
            })}>
            {booking
              ? <><Loader2 size={15} style={{ animation: "xSpin .8s linear infinite" }} /> Booking…</>
              : <><CarFront size={14} /> Confirm &amp; Book Now <ArrowRight size={13} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── TripSummary ────────────────────────────────────────────────────────── */
function TripSummary({ trip, driver, pickupAddr, dropoffAddr, fees, onCancel, onDone, money }: {
  trip: any; driver: Driver; pickupAddr: string; dropoffAddr: string;
  fees: any; onCancel: () => void; onDone: () => void; money: Money;
}) {
  const status  = trip?.status || "requested";
  const cfg     = TRIP_STATUS_CFG[status] || TRIP_STATUS_CFG.requested;
  const isDone  = status === "completed" || status === "cancelled" || status === "no_driver";
  const canCancel = ["requested", "accepted", "finding_driver"].includes(status);

  return (
    <div className="xr-trip-card">
      <div className="xr-trip-status" style={{ borderColor: cfg.color + "40", background: cfg.color + "0D" }}>
        <div className="xr-trip-status-icon">
          {status === "completed"                            ? <Flag size={22} color={cfg.color} /> :
           status === "cancelled" || status === "no_driver"  ? <AlertCircle size={22} color={cfg.color} /> :
           status === "ongoing"                              ? <Navigation2 size={22} color={cfg.color} /> :
                                                                 <Car size={22} color={cfg.color} />}
        </div>
        <div>
          <div className="xr-trip-status-lbl" style={{ color: cfg.color }}>{cfg.label}</div>
          <div className="xr-trip-status-sub">{cfg.sub}</div>
        </div>
        {trip?.etaMinutes && !isDone && (
          <div className="xr-trip-eta-badge" style={{ marginLeft: "auto" }}>
            <Clock size={11} />{trip.etaMinutes} min
          </div>
        )}
      </div>

      <div className="xr-trip-driver-row">
        <div className="xr-trip-av">
          {driver.profilePhoto ? <img src={driver.profilePhoto} alt="" /> : (driver.driverName || driver.fullName || "D")[0]}
        </div>
        <div className="xr-trip-drv-info">
          <div className="xr-trip-drv-name">{driver.driverName || driver.fullName || "Your Driver"}</div>
          <div className="xr-trip-drv-sub">
            {brandName(driver.brand || 0)} {driver.model || ""} · {vehicleTypeName(driver.vehicleType || 0)}
          </div>
          {driver.rating != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, color: "#D4900F", fontSize: 11, fontWeight: 700 }}>
              <Star size={10} fill="#D4900F" color="#D4900F" /> {driver.rating.toFixed(1)}
            </div>
          )}
        </div>
        {driver.phone && (
          <a href={`tel:${driver.phone}`} style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--cr)", border: "1px solid var(--st)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ng)", flexShrink: 0 }}>
            <PhoneCall size={15} />
          </a>
        )}
      </div>

      <div className="xr-trip-route">
        <div className="xr-trip-addr-row">
          <MapPin size={12} color="var(--ng)" style={{ flexShrink: 0 }} />
          <div className="xr-trip-addr-txt">{pickupAddr || "Pickup"}</div>
        </div>
        {dropoffAddr && (
          <div className="xr-trip-addr-row">
            <MapPin size={12} color="var(--rd)" style={{ flexShrink: 0 }} />
            <div className="xr-trip-addr-txt">{dropoffAddr}</div>
          </div>
        )}
      </div>

      <div className="xr-trip-price-row">
        <span className="xr-trip-price-lbl">{status === "completed" ? "Total charged" : "Estimated total"}</span>
        <span className="xr-trip-price-val">{money(fees.totalCharged)}</span>
      </div>

      {canCancel && (
        <button className="xr-trip-cancel-btn" onClick={onCancel}>
          <X size={13} /> Cancel Ride
        </button>
      )}
      {isDone && (
        <button className="xr-trip-done-btn" onClick={onDone}>
          {status === "no_driver" ? <><RotateCcw size={14} /> Try Again</> : <><Home size={14} /> Back to Home</>}
        </button>
      )}
    </div>
  );
}

/* ─── DriversList — shows 5 then "View More" ─────────────────────────────── */
interface DriversListProps {
  drivers: Driver[];
  loading: boolean;
  selDriver: Driver | null;
  nearest: Driver | null;
  proximityTier: "nearby" | "city" | "none" | "no-pickup";
  onBook: (d: Driver) => void;
  money: Money;
}
function DriversList({ drivers, loading, selDriver, nearest, proximityTier, onBook, money }: DriversListProps) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? drivers : drivers.slice(0, DRIVERS_INITIAL);
  const hasMore = !showAll && drivers.length > DRIVERS_INITIAL;

  const headerLabel = proximityTier === "nearby" ? "Available Nearby"
    : proximityTier === "city"    ? "Available in This Area"
    : "Available Now";

  const countLabel = proximityTier === "nearby" ? "within 3 km"
    : proximityTier === "city" ? "within 25 km" : "";

  return (
    <div className="xr-dlist">
      <div className="xr-dlist-hdr">
        <div className="xr-dlist-title">{headerLabel}</div>
        {!loading && drivers.length > 0 && (
          <div className="xr-dlist-cnt">{drivers.length} online{countLabel ? ` · ${countLabel}` : ""}</div>
        )}
      </div>

      {loading ? (
        <div className="xr-loader"><div className="xr-spin-sm" /><div className="xr-loader-txt">Finding drivers…</div></div>
      ) : proximityTier === "no-pickup" ? (
        <div className="xr-empty">
          <div style={{ color: "var(--gdd)", marginBottom: 4 }}><Locate size={38} strokeWidth={1.2} /></div>
          <div className="xr-empty-ttl">Set a pickup point</div>
          <div className="xr-empty-sub">Enter a pickup address or tap the locate button — we'll show drivers near that spot.</div>
        </div>
      ) : drivers.length === 0 ? (
        <div className="xr-empty">
          <div style={{ color: "var(--st)", marginBottom: 4 }}><WifiOff size={42} strokeWidth={1} /></div>
          <div className="xr-empty-ttl">No drivers in this area</div>
          <div className="xr-empty-sub">No active drivers found within 25 km of that pickup point.</div>
        </div>
      ) : (
        <>
          {visible.map((d: Driver) => (
            <DriverCard key={d.driverId} driver={d}
              isSelected={selDriver?.driverId === d.driverId}
              isNearest={nearest?.driverId === d.driverId}
              onBook={() => onBook(d)} money={money} />
          ))}
          {hasMore && (
            <button className="xr-view-more" onClick={() => setShowAll(true)}>
              <ChevronDown size={13} /> View {drivers.length - DRIVERS_INITIAL} More Driver{drivers.length - DRIVERS_INITIAL !== 1 ? "s" : ""}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ─── DraggableSheet ─────────────────────────────────────────────────────── */
type Snap = "peek" | "mid" | "full";
function DraggableSheet({ children, driverCount, loading, hasTrip, proximityTier }: {
  children: React.ReactNode; driverCount: number; loading: boolean; hasTrip: boolean;
  proximityTier: "nearby" | "city" | "none" | "no-pickup";
}) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [snap, setSnap] = useState<Snap>("mid");
  const dragY   = useRef(0);
  const dragTY  = useRef(0);
  const dragging = useRef(false);
  const FRAC: Record<Snap, number> = { peek: 0.13, mid: 0.50, full: 0.92 };

  const applySnap = useCallback((s: Snap) => {
    const el = sheetRef.current; if (!el) return;
    el.style.transition = "transform .38s cubic-bezier(.22,1,.36,1)";
    el.style.transform  = `translateY(${window.innerHeight * (1 - FRAC[s])}px)`;
    setSnap(s);
  }, []); // eslint-disable-line

  useEffect(() => {
    const el = sheetRef.current; if (!el) return;
    el.style.transition = "none";
    el.style.transform  = `translateY(${window.innerHeight * 0.50}px)`;
  }, []); // eslint-disable-line

  useEffect(() => { if (hasTrip) applySnap("full"); }, [hasTrip, applySnap]);

  const onPD = (e: React.PointerEvent) => {
    if (hasTrip) return;
    dragging.current = true; dragY.current = e.clientY;
    const el = sheetRef.current; if (!el) return;
    el.style.transition = "none";
    dragTY.current = new DOMMatrix(getComputedStyle(el).transform).m42;
    el.setPointerCapture(e.pointerId);
  };
  const onPM = (e: React.PointerEvent) => {
    if (!dragging.current || !sheetRef.current) return;
    const dy   = e.clientY - dragY.current;
    const vh   = window.innerHeight;
    const newY = Math.min(vh * (1 - FRAC.peek), Math.max(vh * (1 - FRAC.full), dragTY.current + dy));
    sheetRef.current.style.transform = `translateY(${newY}px)`;
  };
  const onPU = () => {
    if (!dragging.current || !sheetRef.current) return;
    dragging.current = false;
    const curr = new DOMMatrix(getComputedStyle(sheetRef.current).transform).m42;
    const vh   = window.innerHeight;
    const best = (Object.keys(FRAC) as Snap[]).reduce((a, b) =>
      Math.abs(curr - vh * (1 - FRAC[a])) < Math.abs(curr - vh * (1 - FRAC[b])) ? a : b);
    applySnap(best);
  };
  const cycle = () => {
    if (hasTrip) return;
    const o: Snap[] = ["peek", "mid", "full"];
    applySnap(o[(o.indexOf(snap) + 1) % o.length]);
  };

  return (
    <div ref={sheetRef} className="xr-mob-sheet">
      <div onPointerDown={onPD} onPointerMove={onPM} onPointerUp={onPU} onPointerCancel={onPU}
        style={{ touchAction: "none", cursor: hasTrip ? "default" : "grab", userSelect: "none", paddingBottom: 4 }}>
        <div className="xr-sheet-handle" />
        <div className="xr-sheet-peek">
          <div className="xr-sheet-peek-l">
            <Car size={15} color="#D4900F" />
            <div>
              <div className="xr-sheet-ttl">
                {hasTrip ? "Your Ride"
                  : loading ? "Finding drivers…"
                  : proximityTier === "no-pickup" ? "Set a pickup point"
                  : driverCount > 0
                    ? `${driverCount} driver${driverCount > 1 ? "s" : ""} ${proximityTier === "city" ? "in your area" : "nearby"}`
                  : "No drivers in this area"}
              </div>
              <div className="xr-sheet-sub">{hasTrip ? "Live trip status" : "Drag to resize"}</div>
            </div>
          </div>
          {!hasTrip && (
            <button className="xr-expand" onClick={cycle}>
              {snap === "full" ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
              {snap === "full" ? "Collapse" : snap === "mid" ? "Expand" : "Open"}
            </button>
          )}
        </div>
      </div>
      <div ref={innerRef} className="xr-sheet-scroll" style={{
        maxHeight: `${FRAC[snap] * 100}dvh`,
        overflowY: snap === "peek" ? "hidden" : "auto",
        transition: "max-height .38s cubic-bezier(.22,1,.36,1)",
      }}>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN COMPONENT                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function BookRide() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: RootState) => s.auth);

  // ── Fees — read the slice's own state directly. There is no `selectServiceFee`
  //    selector exported anywhere in riderServiceFeeSlice.ts; importing it was
  //    a silent runtime crash waiting to happen. Adjust the key below ("riderServiceFees")
  //    if your root reducer registers this slice under a different name. ──
  const fees = useSelector((s: RootState) => (s as any).riderServiceFees);

  // ── Cosmoola currency — every price in this page is stored/computed in EUR
  //    server-side (matches the ShortAccomodationPreview convention) and
  //    converted for display only. ──
  const { convertRaw, symbol } = useCurrency();
  const money: Money = useCallback((amountEur) => {
    const n = Number(amountEur ?? 0);
    return `${symbol}${convertRaw(n, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [convertRaw, symbol]);

  const params: any = (location.state as any) || {};
  const userId = params.userId || (user as any)?.userId || (user as any)?.uid || "guest";

  /* Maps */
  const [isLoaded,  setIsLoaded]  = useState(typeof (window as any).google !== "undefined");
  const mapRef        = useRef<any>(null);
  const markersRef    = useRef<any[]>([]);
  const routeRendRef  = useRef<any>(null);
  const driverPolyRef = useRef<any>(null);
  const [mapReady, setMapReady]   = useState(false);
  const deskFormRef = useRef<AddressFormHandle>(null);
  const mobFormRef  = useRef<AddressFormHandle>(null);

  /* State */
  const [userLoc,       setUserLoc]       = useState<LatLng | null>(null);
  const [gpsReady,      setGpsReady]      = useState(false);
  const [pickupAddr,    setPickupAddr]    = useState(params.pickup || "");
  const [pickupCoords,  setPickupCoords]  = useState<LatLng | null>(params.pickupCoords || null);
  const [dropoffAddr,   setDropoffAddr]   = useState(params.dropoff || "");
  const [dropoffCoords, setDropoffCoords] = useState<LatLng | null>(params.dropoffCoords || null);
  const [forOther,      setForOther]      = useState(false);
  const [listings,      setListings]      = useState<any[]>([]);
  const [livePos,       setLivePos]       = useState<Record<string, any>>({});
  const [loading,       setLoading]       = useState(true);
  const [activeCat,     setActiveCat]     = useState<number | null>(null);
  const [selDriver,     setSelDriver]     = useState<Driver | null>(null);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [driverEtaMin,  setDriverEtaMin]  = useState<number | null>(null);
  const [tripEtaMin,    setTripEtaMin]    = useState<number | null>(null);
  const [booking,       setBooking]       = useState(false);
  const [bookingErr,    setBookingErr]    = useState("");
  const [activeTrip,    setActiveTrip]    = useState<any>(null);
  const [bookedDriver,  setBookedDriver]  = useState<Driver | null>(null);

  // ── Robustness additions ──
  const [rtdbOffline,   setRtdbOffline]   = useState(false);   // RTDB ".info/connected" watcher
  const [resumeNotice,  setResumeNotice]  = useState<{ driverName: string } | null>(null); // cancelled-checkout recovery
  const submittingRef   = useRef(false);                        // hard lock — state alone can race on double-click
  const idempotencyKeyRef = useRef<string>("");                 // one key per booking attempt lifecycle

  /* CSS inject */
  useEffect(() => {
    const id = "heartlas-br-v2"; if (document.getElementById(id)) return;
    const el = document.createElement("style"); el.id = id; el.textContent = CSS; document.head.appendChild(el);
  }, []);

  /* ── Return-from-Stripe: pick up ?tripId= from the success_url and reattach
        the live trip listener. Also handles ?checkout=cancelled by restoring
        the driver the rider had selected (stashed in sessionStorage right
        before the redirect to Stripe). Both params are stripped from the URL
        afterwards so a refresh doesn't replay them. ── */
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const tripIdParam = qs.get("tripId");
    const checkoutParam = qs.get("checkout");

    if (tripIdParam) {
      setActiveTrip({ tripId: tripIdParam, status: "requested" });
    }

    if (checkoutParam === "cancelled") {
      try {
        const raw = sessionStorage.getItem(PENDING_RIDE_KEY);
        if (raw) {
          const pending = JSON.parse(raw);
          setResumeNotice({ driverName: pending?.driverName || "your driver" });
        }
      } catch { /* ignore malformed sessionStorage */ }
    }

    if (tripIdParam || checkoutParam) {
      qs.delete("tripId"); qs.delete("checkout"); qs.delete("session_id");
      const clean = qs.toString();
      navigate({ pathname: location.pathname, search: clean ? `?${clean}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fallback resume: if we land here fresh with no tripId in the URL but
        the rider has a trip mid-flight (e.g. they closed the tab after
        payment before the redirect finished), pick it up from a pointer node
        your webhook is expected to maintain at `activeRiderTrip/{userId}`.
        This assumes that shape — adjust the path if your backend differs. ── */
  useEffect(() => {
    if (activeTrip?.tripId || !userId || userId === "guest") return;
    (async () => {
      try {
        const snap = await dbGet(dbRef(rtdb, `activeRiderTrip/${userId}`));
        const pointerId = snap.exists() ? snap.val()?.tripId ?? snap.val() : null;
        if (pointerId) setActiveTrip({ tripId: pointerId, status: "requested" });
      } catch { /* pointer node may simply not exist yet — that's fine */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /* ── RTDB connectivity — surfaces dropped sockets instead of failing silently ── */
  useEffect(() => {
    const r = dbRef(rtdb, ".info/connected");
    const cb = onValue(r, snap => setRtdbOffline(snap.val() !== true));
    return () => off(r, "value", cb as any);
  }, []);

  /* GPS + reverse geocode */
  useEffect(() => {
    if (!navigator.geolocation) { setGpsReady(true); return; }
    navigator.geolocation.getCurrentPosition(async p => {
      const l = { lat: p.coords.latitude, lng: p.coords.longitude };
      setUserLoc(l);
      if (!pickupCoords) {
        setPickupCoords(l);
        const waitForGoogle = () => new Promise<void>(resolve => {
          if (typeof (window as any).google !== "undefined") { resolve(); return; }
          const t = setInterval(() => { if (typeof (window as any).google !== "undefined") { clearInterval(t); resolve(); } }, 100);
        });
        await waitForGoogle();
        const addr = await reverseGeocode(l.lat, l.lng);
        setPickupAddr(addr);
      }
      setGpsReady(true);
      if (mapRef.current) mapRef.current.panTo(l);
    }, () => setGpsReady(true), { timeout: 8000, enableHighAccuracy: false });
  }, []); // eslint-disable-line

  /* Poll Google Maps ready */
  useEffect(() => {
    if (isLoaded) return;
    const t = setInterval(() => {
      if (typeof (window as any).google !== "undefined") { setIsLoaded(true); clearInterval(t); }
    }, 100);
    return () => clearInterval(t);
  }, [isLoaded]);

  /* Init map */
  useEffect(() => {
    if (!isLoaded) return;
    const el = document.getElementById("xr-gmap"); if (!el || mapRef.current) return;
    const g = (window as any).google.maps;
    const center = userLoc || pickupCoords || DEFAULT_CENTER;
    mapRef.current = new g.Map(el, {
      center, zoom: (userLoc || pickupCoords) ? 14 : 11,
      styles: MAP_STYLE, disableDefaultUI: true, zoomControl: true,
      zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
      gestureHandling: "greedy",
    });
    setMapReady(true);
  }, [isLoaded]); // eslint-disable-line

  useEffect(() => {
    if (mapRef.current && userLoc && mapReady) {
      mapRef.current.panTo(userLoc);
      if (mapRef.current.getZoom() < 13) mapRef.current.setZoom(14);
    }
  }, [userLoc, mapReady]);

  /* Autocomplete */
  const acInstances = useRef<any[]>([]);
  useEffect(() => {
    if (!isLoaded) return;
    const g = (window as any).google.maps.places;
    acInstances.current.forEach(ac => { try { (window as any).google.maps.event.clearInstanceListeners(ac); } catch { } });
    acInstances.current = [];
    const attach = (inputEl: HTMLInputElement | null, onPlace: (a: string, la: number, lo: number) => void) => {
      if (!inputEl) return null;
      const ac = new g.Autocomplete(inputEl, { types: ["geocode", "establishment"] });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place?.geometry?.location)
          onPlace(place.formatted_address || place.name || "", place.geometry.location.lat(), place.geometry.location.lng());
      });
      return ac;
    };
    const sp = (a: string, la: number, lo: number) => { setPickupAddr(a); setPickupCoords({ lat: la, lng: lo }); };
    const sd = (a: string, la: number, lo: number) => { setDropoffAddr(a); setDropoffCoords({ lat: la, lng: lo }); };
    acInstances.current = [
      attach(deskFormRef.current?.pickupInput  ?? null, sp),
      attach(deskFormRef.current?.dropoffInput ?? null, sd),
      attach(mobFormRef.current?.pickupInput   ?? null, sp),
      attach(mobFormRef.current?.dropoffInput  ?? null, sd),
    ].filter(Boolean);
  }, [isLoaded]);

  useEffect(() => {
    [deskFormRef.current?.pickupInput, mobFormRef.current?.pickupInput].forEach(el => {
      if (el && el !== document.activeElement) el.value = pickupAddr;
    });
  }, [pickupAddr]);
  useEffect(() => {
    [deskFormRef.current?.dropoffInput, mobFormRef.current?.dropoffInput].forEach(el => {
      if (el && el !== document.activeElement) el.value = dropoffAddr;
    });
  }, [dropoffAddr]);

  /* RTDB live positions */
  useEffect(() => {
    const r = dbRef(rtdb, "onlineDrivers");
    onValue(r, snap => setLivePos(snap.val() || {}));
    return () => off(r, "value");
  }, []);

  /* Trip status listener — also backfills bookedDriver on a resumed session
     where the trip node carries a driver snapshot but selDriver is empty
     (e.g. after a fresh page load from the Stripe redirect). */
  useEffect(() => {
    if (!activeTrip?.tripId || !userId) return;
    const r = dbRef(rtdb, `riderTrips/${userId}/${activeTrip.tripId}`);
    const cb = onValue(r, snap => {
      const v = snap.val();
      if (!v) return;
      setActiveTrip((prev: any) => ({ ...prev, ...v }));
      setBookedDriver(prev => {
        if (prev) return prev;
        if (v.driverSnapshot) return v.driverSnapshot as Driver;
        return prev;
      });
    });
    return () => off(r, "value", cb as any);
  }, [activeTrip?.tripId, userId]);

  /* Firestore listings */
/* Firestore listings — live subscription so isSelected/driverId flips
   (host reassigns a vehicle, driver logs off) reach the rider without
   a page reload. */
useEffect(() => {
  setLoading(true);
  const q = query(collection(db, "Fleet"), where("isPublished", "==", true));
  const unsub = onSnapshot(
    q,
    snap => {
      setListings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    },
    err => {
      console.error("Fleet listener failed:", err);
      setLoading(false);
    }
  );
  return () => unsub();
}, []);

  /* Reference point for proximity */
  const refPoint: LatLng | null = useMemo(() => {
    if (forOther) return pickupCoords || null;
    return userLoc || pickupCoords || null;
  }, [forOther, pickupCoords, userLoc]);

  /* Merge listings + live positions */
  const allMerged: Driver[] = useMemo(() => {
    const result: Driver[] = [];
    for (const listing of listings) {
      // Only show a vehicle if someone has actually claimed it as their active
      // vehicle (Uber-style) — a published listing nobody is driving right
      // now shouldn't be bookable.
      if (!listing.isSelected || !listing.driverId) continue;

      // RTDB online status is keyed by listingId (see DriverCars: onlineDrivers/{listingId}),
      // not hostId — hostId is the fleet owner and is unrelated to this key.
      const lid  = listing.listingId || listing.id;
      const live = livePos[lid];
      const hasLive = live && typeof live.lat === "number" && typeof live.lng === "number";
      // Only show it if it's genuinely online right now (Uber-style) — don't
      // fall back to a possibly stale last-known Firestore lat/lng.
      if (!hasLive) continue;
      const pos: LatLng = { lat: live.lat, lng: live.lng };
      const distKm = refPoint ? haversineKm(refPoint.lat, refPoint.lng, pos.lat, pos.lng) : null;
      const eta    = distKm !== null ? Math.max(1, Math.round((distKm / 40) * 60)) : null;
      result.push({
        ...listing, listingId: listing.id,
        hostId:   listing.hostId,     // fleet owner — separate from driverId
        driverId: listing.driverId,   // the actual person currently driving this vehicle
        rateType: resolveRateType(listing.rateType),
        livePos: pos, heading: live?.heading || 0,
        distKm: distKm ?? undefined, eta: eta ?? undefined,
      });
    }
    if (refPoint) result.sort((a, b) => (a.distKm ?? 9999) - (b.distKm ?? 9999));
    return result;
  }, [listings, livePos, refPoint]);

  /* Proximity filter */
  const merged: Driver[] = useMemo(() => {
    if (!refPoint) return [];
    const within3  = allMerged.filter(d => (d.distKm ?? 9999) <= 3);
    if (within3.length > 0) return within3;
    return allMerged.filter(d => (d.distKm ?? 9999) <= 25);
  }, [allMerged, refPoint]);

  const drivers: Driver[] = useMemo(() =>
    activeCat === null ? merged : merged.filter(d => d.vehicleType === activeCat)
  , [merged, activeCat]);

  const nearest = drivers[0] || null;

  const proximityTier: "nearby" | "city" | "none" | "no-pickup" = useMemo(() => {
    if (!refPoint) return "no-pickup";
    if (allMerged.filter(d => (d.distKm ?? 9999) <= 3).length > 0)  return "nearby";
    if (allMerged.filter(d => (d.distKm ?? 9999) <= 25).length > 0) return "city";
    return "none";
  }, [allMerged, refPoint]);

  const availTypes = useMemo(() => {
    const s = new Set<number>(); merged.forEach(d => { if (d.vehicleType !== undefined) s.add(d.vehicleType); });
    return ALL_VEHICLE_TYPES.filter((v: any) => s.has(v.id));
  }, [merged]);

  const distanceKmToDropoff = useMemo(
    () => (pickupCoords && dropoffCoords ? haversineKm(pickupCoords.lat, pickupCoords.lng, dropoffCoords.lat, dropoffCoords.lng) : 0),
    [pickupCoords, dropoffCoords]
  );

  /* Live fee preview — fires whenever the selected driver or route changes.
     `extraCharges` is now a real number (was silently dropped before),
     and perSurge cases pass through base/perKm/perMinute/surge fields. */
  useEffect(() => {
    if (!selDriver) return;
    const rt = resolveRateType(selDriver.rateType);
    dispatch(calculateRideEarnings({
      rate:            selDriver.rate || 0,
      rateType:        rt,
      distanceKm:      distanceKmToDropoff,
      passengers:      1,
      seats:           1,
      extraCharges:    0, // no extras selected yet at preview time
      discountApplied: selDriver.discountApplied || {},
      baseFare:        selDriver.baseFare || 0,
      perKmRate:       selDriver.perKmRate || 0,
      perMinuteRate:   selDriver.perMinuteRate || 0,
      durationMinutes: tripEtaMin || 0,
      surgeMultiplier: selDriver.surgeMultiplier || 1,
    }));
  }, [selDriver, distanceKmToDropoff, tripEtaMin, dispatch]);

  /* Map helpers */
  const destroyRouteRenderer = useCallback(() => {
    if (routeRendRef.current) { routeRendRef.current.setMap(null); routeRendRef.current = null; }
  }, []);
  const getOrCreateRouteRenderer = useCallback(() => {
    if (!routeRendRef.current) {
      const g = (window as any).google.maps;
      routeRendRef.current = new g.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#C8202E", strokeOpacity: .9, strokeWeight: 4 },
      });
      routeRendRef.current.setMap(mapRef.current);
    }
    return routeRendRef.current;
  }, []);

  /* updateMap */
  const updateMap = useCallback(async () => {
    if (!mapRef.current || !isLoaded || !mapReady) return;
    const g = (window as any).google.maps;
    markersRef.current.forEach(m => m.setMap(null)); markersRef.current = [];
    if (driverPolyRef.current) { driverPolyRef.current.setMap(null); driverPolyRef.current = null; }

    if (pickupCoords) {
      markersRef.current.push(new g.Marker({
        position: pickupCoords, map: mapRef.current, zIndex: 12,
        icon: { url: mkPickup(), scaledSize: new g.Size(32, 44), anchor: new g.Point(16, 44) },
      }));
    }
    if (dropoffCoords) {
      markersRef.current.push(new g.Marker({
        position: dropoffCoords, map: mapRef.current, zIndex: 12,
        icon: { url: mkDropoff(), scaledSize: new g.Size(32, 44), anchor: new g.Point(16, 44) },
      }));
      if (pickupCoords) {
        const renderer = getOrCreateRouteRenderer();
        new g.DirectionsService().route(
          { origin: pickupCoords, destination: dropoffCoords, travelMode: g.TravelMode.DRIVING },
          (result: any, status: any) => {
            if (status === "OK") {
              renderer.setDirections(result);
              const leg = result.routes?.[0]?.legs?.[0];
              if (leg?.duration?.value) setTripEtaMin(Math.round(leg.duration.value / 60));
            }
          }
        );
      }
    } else { destroyRouteRenderer(); setTripEtaMin(null); }

    drivers.forEach(d => {
      const dim = d.carSize === CAR_SIZE.XL || d.carSize === CAR_SIZE.XXL ? 56 : 44;
      markersRef.current.push(new g.Marker({
        position: { lat: d.livePos.lat, lng: d.livePos.lng },
        map: mapRef.current,
        zIndex: nearest?.driverId === d.driverId ? 20 : 5,
        icon: { url: IMG_CAR, scaledSize: new g.Size(dim, dim), anchor: new g.Point(dim / 2, dim / 2) },
      }));
    });

    const origin = userLoc || pickupCoords;
    if (origin && nearest) {
      new g.DirectionsService().route(
        { origin, destination: { lat: nearest.livePos.lat, lng: nearest.livePos.lng }, travelMode: g.TravelMode.DRIVING },
        (result: any, status: any) => {
          if (driverPolyRef.current) { driverPolyRef.current.setMap(null); driverPolyRef.current = null; }
          if (status === "OK" && result.routes?.[0]?.overview_path) {
            driverPolyRef.current = new g.Polyline({
              path: result.routes[0].overview_path, map: mapRef.current,
              strokeColor: "#1B0E06", strokeOpacity: 0, strokeWeight: 0,
              icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3.5, strokeColor: "#1B0E06" }, offset: "0", repeat: "14px" }],
            });
            const leg = result.routes[0]?.legs?.[0];
            if (leg?.duration?.value) setDriverEtaMin(Math.round(leg.duration.value / 60));
          } else if (nearest.eta) setDriverEtaMin(nearest.eta);
        }
      );
    } else setDriverEtaMin(nearest?.eta ?? null);

    const coords: LatLng[] = [];
    if (userLoc) coords.push(userLoc);
    if (pickupCoords) coords.push(pickupCoords);
    if (dropoffCoords) coords.push(dropoffCoords);
    drivers.slice(0, 6).forEach(d => coords.push({ lat: d.livePos.lat, lng: d.livePos.lng }));
    if (coords.length >= 2) {
      const b = new g.LatLngBounds(); coords.forEach(c => b.extend(c));
      mapRef.current.fitBounds(b, { top: 60, bottom: 60, left: 50, right: 50 });
    } else if (coords.length === 1) mapRef.current.panTo(coords[0]);
  }, [isLoaded, mapReady, drivers, pickupCoords, dropoffCoords, userLoc, nearest,
    getOrCreateRouteRenderer, destroyRouteRenderer]);

  useEffect(() => { updateMap(); }, [updateMap]);

  /* ── Stripe response handling — identical shape-tolerance to
        ShortAccomodationPreview's redirectFromResponse. ── */
  type CheckoutResponse = { id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string };
  const redirectFromResponse = (data: CheckoutResponse) => {
    const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
    if (directUrl) { window.location.href = directUrl; return; }
    if (data?.id)  { window.location.href = `https://checkout.stripe.com/c/pay/${data.id}`; return; }
    throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);
  };

  /* ── handleConfirm ──
     - extraCharges is now computed (was silently ignored before)
     - hard submit lock via ref, not just disabled-button state
     - idempotency key sent so a retried/duplicated request can't double-charge
     - selected driver stashed in sessionStorage right before the redirect so
       a cancelled checkout can offer to resume it */
  const handleConfirm = useCallback(async (opts: any) => {
    if (!selDriver || !pickupCoords) return;
    if (submittingRef.current) return; // hard lock — beats any state-update race
    submittingRef.current = true;

    setBookingErr("");
    setBooking(true);

    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = makeIdempotencyKey();

    const rt = resolveRateType(selDriver.rateType);
    const extraCharges = sumSelectedExtras(selDriver.extras, opts.selectedExtras || []);
    const distKm = distanceKmToDropoff;
    const durationMinutes = tripEtaMin || Math.round((distKm / 40) * 60);

    // Recalculate the *displayed* estimate with the final selections.
    // The actual charge is still computed and enforced server-side.
    dispatch(calculateRideEarnings({
      rate:            selDriver.rate || 0,
      rateType:        rt,
      distanceKm:      distKm,
      passengers:      opts.passengers || 1,
      seats:           opts.seats || 1,
      extraCharges,
      discountApplied: selDriver.discountApplied || {},
      durationDays:    opts.durationDays || 0,
      baseFare:        selDriver.baseFare || 0,
      perKmRate:       selDriver.perKmRate || 0,
      perMinuteRate:   selDriver.perMinuteRate || 0,
      durationMinutes,
      surgeMultiplier: selDriver.surgeMultiplier || 1,
    }));

    try {
      sessionStorage.setItem(PENDING_RIDE_KEY, JSON.stringify({
        driverId: selDriver.driverId,
        driverName: selDriver.driverName || selDriver.fullName || "your driver",
        savedAt: Date.now(),
      }));
    } catch { /* sessionStorage may be unavailable (private mode) — non-fatal */ }

    try {
      const payload = {
        idempotencyKey: idempotencyKeyRef.current,

        riderId:   userId,
        listingId: selDriver.listingId,
        hostId:    selDriver.hostId || "",     // fleet owner — for payout/reporting aggregation
        driverId:  selDriver.driverId || "",   // the actual person driving this specific vehicle
        brandId:   selDriver.brandId || "",

        pickupLat:      pickupCoords.lat,
        pickupLng:      pickupCoords.lng,
        pickupAddress:  pickupAddr,
        dropoffLat:     dropoffCoords?.lat ?? null,
        dropoffLng:     dropoffCoords?.lng ?? null,
        dropoffAddress: dropoffAddr || "",

        passengers:     opts.passengers || 1,
        adults:         opts.adults || 1,
        children:       opts.children || 0,
        infants:        opts.infants || 0,
        pets:           opts.pets || 0,
        serviceAnimals: opts.serviceAnimals || 0,
        seats:          opts.seats || 1,

        rateType:        rt,
        rate:            selDriver.rate || 0,
        distanceKm:      distKm,
        durationMinutes,
        durationDays:    opts.durationDays || 0,
        baseFare:        selDriver.baseFare || 0,
        perKmRate:       selDriver.perKmRate || 0,
        perMinuteRate:   selDriver.perMinuteRate || 0,
        surgeMultiplier: selDriver.surgeMultiplier || 1,

        selectedExtras:  opts.selectedExtras || [],
        discountApplied: selDriver.discountApplied || {},

        creditsAvailable: 0,
        creditsUsed:      0,

        rideSharing: selDriver.rideSharing || false,
        notes:       "",

        // Success/cancel URLs — the backend is expected to substitute the real
        // tripId into successUrl once it creates the trip record, e.g.
        // `${successUrl}&tripId=${tripId}&session_id={CHECKOUT_SESSION_ID}`.
        successUrl: `${window.location.origin}${window.location.pathname}?checkout=success`,
        cancelUrl:  `${window.location.origin}${window.location.pathname}?checkout=cancelled`,
      };

      const res = await axios.post<CheckoutResponse>(RIDE_CHECKOUT_URL, payload, {
        headers: { "Idempotency-Key": idempotencyKeyRef.current },
      });
      redirectFromResponse(res.data);
      // Note: we deliberately do NOT reset submittingRef/booking on the happy
      // path — the browser is about to navigate away to Stripe.
    } catch (e: any) {
      console.error("❌ Checkout error:", e?.response?.data || e?.message);
      const msg = e?.response?.data?.error?.message || e?.message || "Booking failed. Please try again.";
      setBookingErr(msg);
      setBooking(false);
      submittingRef.current = false;
      idempotencyKeyRef.current = ""; // allow a fresh key on manual retry
    }
  }, [selDriver, pickupCoords, dropoffCoords, pickupAddr, dropoffAddr, userId, dispatch, distanceKmToDropoff, tripEtaMin]);

  /* Cancel trip */
  const handleCancelTrip = useCallback(async () => {
    if (!activeTrip?.tripId) return;
    try {
      await axios.post(CANCEL_RIDE_URL, {
        tripId: activeTrip.tripId, cancelledBy: userId, reason: "rider_cancelled",
      });
      setActiveTrip(null); setBookedDriver(null);
    } catch (e: any) { console.error("Cancel failed:", e.message); }
  }, [activeTrip, userId]);

  const handleDone = useCallback(() => {
    const status = activeTrip?.status;
    setActiveTrip(null); setBookedDriver(null);
    if (status !== "no_driver") navigate("/");
  }, [activeTrip, navigate]);

  /* Locate me */
  const locateMe = useCallback(async () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async p => {
      const l = { lat: p.coords.latitude, lng: p.coords.longitude };
      setUserLoc(l); setPickupCoords(l);
      if (mapRef.current) { mapRef.current.panTo(l); mapRef.current.setZoom(14); }
      const addr = await reverseGeocode(l.lat, l.lng);
      setPickupAddr(addr);
    });
  }, []);

  const swapAddresses = () => {
    const ta = pickupAddr, tc = pickupCoords;
    setPickupAddr(dropoffAddr); setPickupCoords(dropoffCoords);
    setDropoffAddr(ta); setDropoffCoords(tc);
  };

  const dismissResumeNotice = () => {
    setResumeNotice(null);
    try { sessionStorage.removeItem(PENDING_RIDE_KEY); } catch { /* ignore */ }
  };
  const resumeCancelledBooking = () => {
    const targetName = resumeNotice?.driverName;
    dismissResumeNotice();
    const match = merged.find(d =>
      (d.driverName || d.fullName) === targetName
    ) || nearest;
    if (match) { setSelDriver(match); setModalOpen(true); setBookingErr("Your previous checkout was cancelled — review and try again."); }
  };

  /* Derived */
  const dominantRT = merged.length > 0 ? resolveRateType(merged[0].rateType) : null;
  const hasTrip    = !!activeTrip;

  const formProps = {
    pickupAddr, dropoffAddr, forOther, activeCat, availTypes,
    onPickupChange: setPickupAddr, onDropoffChange: setDropoffAddr,
    onForOtherToggle: () => setForOther(f => !f),
    onLocateMe: locateMe, onSwap: swapAddresses, onCatChange: setActiveCat,
  };

  /* Panel shared content */
  const SearchPanel = (
    <>
      <div className="xr-panel-top">
        <div className="xr-eyebrow">HEARTLAS RIDES</div>
        <div className="xr-panel-title">Where to?</div>
        <div className="xr-panel-sub">Drivers appear before you set a destination</div>
        <AddressForm ref={deskFormRef} {...formProps} />
      </div>
      <div className="xr-section-div">
        <div className="xr-div-line" />
        {dominantRT && <div className="xr-rate-badge"><Sparkles size={9} />{RATE_LABEL[dominantRT]}</div>}
        <div className="xr-div-line" />
      </div>
      <DriversList drivers={drivers} loading={loading} selDriver={selDriver} nearest={nearest}
        proximityTier={proximityTier} money={money}
        onBook={d => { setSelDriver(d); setModalOpen(true); setBookingErr(""); idempotencyKeyRef.current = ""; }} />
    </>
  );

  const TripPanel = bookedDriver && activeTrip && (
    <TripSummary
      trip={activeTrip} driver={bookedDriver}
      pickupAddr={pickupAddr} dropoffAddr={dropoffAddr}
      fees={fees} money={money}
      onCancel={handleCancelTrip}
      onDone={handleDone}
    />
  );

  const MobileSearch = (
    <>
      <div style={{ padding: "16px 16px 0" }}>
        <div className="xr-eyebrow" style={{ marginBottom: 4 }}>HEARTLAS RIDES</div>
        <div className="xr-panel-title" style={{ fontSize: 22, marginBottom: 3 }}>Where to?</div>
        <div className="xr-panel-sub">Drivers appear before you set a destination</div>
        <AddressForm ref={mobFormRef} {...formProps} />
      </div>
      <div className="xr-section-div" style={{ paddingLeft: 16, paddingRight: 16 }}>
        <div className="xr-div-line" />
        {dominantRT && <div className="xr-rate-badge"><Sparkles size={9} />{RATE_LABEL[dominantRT]}</div>}
        <div className="xr-div-line" />
      </div>
      <DriversList drivers={drivers} loading={loading} selDriver={selDriver} nearest={nearest}
        proximityTier={proximityTier} money={money}
        onBook={d => { setSelDriver(d); setModalOpen(true); setBookingErr(""); idempotencyKeyRef.current = ""; }} />
    </>
  );

  return (
    <div className="xr">
      <div className="xr-topbar" />

      {rtdbOffline && (
        <div className="xr-banner xr-banner-offline">
          <CloudOff size={13} /> Reconnecting to live driver data…
        </div>
      )}

      {resumeNotice && !hasTrip && (
        <div className="xr-banner xr-banner-resume">
          <Info size={13} />
          Your last checkout with {resumeNotice.driverName} didn't finish.
          <button onClick={resumeCancelledBooking}>Resume</button>
          <button className="xr-banner-close" onClick={dismissResumeNotice}><X size={13} /></button>
        </div>
      )}

      <header className="xr-hdr">
        <div className="xr-logo" onClick={() => navigate("/")}>
          <div className="xr-logo-ring"><div className="xr-logo-ring-inner"><Sparkles size={13} color="#D4900F" /></div></div>
          <span className="xr-logo-x">HEARTLAS</span>
        </div>
        <div className="xr-hdr-c">
          <button className="xr-back" onClick={() => navigate(-1)}><ChevronLeft size={16} /></button>
          <span className="xr-hdr-lbl">{hasTrip ? "Your Ride" : "Book a Ride"}</span>
          {!loading && drivers.length > 0 && !hasTrip && (
            <div className="xr-live">
              <div className="xr-live-dot" />
              <span className="xr-live-txt">{drivers.length} Live</span>
            </div>
          )}
        </div>
        <div className="xr-hdr-r"><button className="xr-ibtn"><Bell size={14} /></button></div>
      </header>

      <div className="xr-body">
        <div className="xr-panel-col">
          {hasTrip ? TripPanel : SearchPanel}
        </div>

        <div className="xr-map-col">
          <div className="xr-map-wrap">
            <div id="xr-gmap" style={{ width: "100%", height: "100%" }} />
            {(!isLoaded || !gpsReady) && <div className="xr-map-loading"><div className="xr-map-spin" /></div>}
            <div className="xr-overlays">
              <div className="xr-eta-col">
                {driverEtaMin !== null && (
                  <div className="xr-eta-pill">
                    <Clock size={13} color="#D4900F" />
                    <div><div className="xr-eta-num">{driverEtaMin} min</div><div className="xr-eta-lbl">driver arrives</div></div>
                  </div>
                )}
                {tripEtaMin !== null && (
                  <div className="xr-eta-pill xr-eta-pill-red">
                    <Navigation2 size={13} color="#C8202E" />
                    <div><div className="xr-eta-num">{tripEtaMin} min</div><div className="xr-eta-lbl">trip duration</div></div>
                  </div>
                )}
              </div>
              <div>
                {!loading && drivers.length > 0 && !hasTrip && (
                  <div className="xr-cnt-pill">
                    <div className="xr-live-dot" style={{ width: 7, height: 7, background: "#fff" }} />
                    <span className="xr-cnt-txt">{drivers.length} online</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DraggableSheet driverCount={drivers.length} loading={loading} hasTrip={hasTrip} proximityTier={proximityTier}>
          {hasTrip ? TripPanel : MobileSearch}
        </DraggableSheet>
      </div>

      {modalOpen && selDriver && (
        <BookingModal
          driver={selDriver}
          fees={fees}
          booking={booking}
          bookingErr={bookingErr}
          money={money}
          onClose={() => { if (!booking) { setModalOpen(false); setSelDriver(null); setBookingErr(""); } }}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}